import { useEffect, useState } from "react";
import { PackageX } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { appDialogSecondaryButton } from "@/components/ui/app-dialog";
import { useAuth } from "@/hooks/auth";
import {
  acknowledgeExpiredSellerStock,
  ExpiredSellerStock,
  getExpiredSellerStock,
} from "@/Api/stockAlerts";

const POLLING_INTERVAL_MS = 5 * 60 * 1000;

const formatDate = (value?: string | null) => {
  if (!value) return "-";
  const [y, m, d] = value.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

/**
 * Aviso diário de bolões vencidos no caixa do vendedor (LOT-52).
 *
 * Mesmo comportamento do aviso de devolução e do alerta de estoque: interrompe a tela e só fecha
 * em "Ciente". Depois do Ciente não reabre no mesmo dia; volta no dia seguinte enquanto houver
 * bolão de concurso vencido no estoque do vendedor.
 */
export function ExpiredSellerStockModal() {
  const auth = useAuth();
  const navigate = useNavigate();
  const isImpersonating = !!auth?.impersonation;
  const isSeller = !!auth?.user?.seller?.id;

  const [pending, setPending] = useState<ExpiredSellerStock | null>(null);
  const [acknowledging, setAcknowledging] = useState(false);

  const fetchPending = async () => {
    try {
      const { data, ok } = await getExpiredSellerStock();
      if (ok) setPending(data?.response ?? null);
    } catch (error) {
      console.error("Erro ao buscar bolões vencidos no caixa:", error);
    }
  };

  useEffect(() => {
    // Admin personificando não pode dar Ciente pelo vendedor
    if (isImpersonating || !isSeller) return;

    const refetchWhenVisible = () => {
      if (document.visibilityState === "visible") fetchPending();
    };

    fetchPending();
    const interval = setInterval(fetchPending, POLLING_INTERVAL_MS);
    // Aba aberta de um dia para o outro: busca de novo quando o usuário voltar
    document.addEventListener("visibilitychange", refetchWhenVisible);
    window.addEventListener("focus", refetchWhenVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", refetchWhenVisible);
      window.removeEventListener("focus", refetchWhenVisible);
    };
  }, [isImpersonating, isSeller]);

  const acknowledge = async (thenOpenStock = false) => {
    setAcknowledging(true);
    try {
      const { ok } = await acknowledgeExpiredSellerStock();
      if (!ok) return;
      setPending(null);
      if (thenOpenStock) navigate("/estoque");
    } catch (error) {
      console.error("Erro ao confirmar aviso de bolões vencidos:", error);
      toast.error("Não foi possível confirmar. Tente novamente.");
    } finally {
      setAcknowledging(false);
    }
  };

  if (isImpersonating || !isSeller || !pending) return null;

  return (
    <AlertDialog open>
      <AlertDialogContent className="max-h-[92vh] max-w-lg gap-0 overflow-hidden p-0">
        <div className="flex items-center gap-4 bg-red-600 px-4 py-5 text-white sm:px-6">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-white/20 sm:h-14 sm:w-14">
            <PackageX className="h-8 w-8 sm:h-9 sm:w-9" strokeWidth={2.25} />
          </span>
          <AlertDialogHeader className="space-y-0.5 text-left">
            <AlertDialogTitle className="text-xl font-bold sm:text-2xl">Bolões vencidos no caixa</AlertDialogTitle>
            <p className="text-sm text-white/90">
              Esses bolões ainda estão no seu caixa de concursos que já passou a data de validade.
            </p>
          </AlertDialogHeader>
        </div>

        <AlertDialogDescription asChild className="text-foreground">
          <div className="max-h-[50vh] space-y-3 overflow-y-auto px-4 py-5 sm:px-6">
            <p className="text-sm text-muted-foreground">
              Total no caixa: <strong className="text-foreground">{pending.total_quantity}</strong>{" "}
              {pending.total_quantity === 1 ? "bolão" : "bolões"}. Este aviso aparece todos os dias até a
              regularização.
            </p>
            {pending.contests.map((contest) => (
              <div key={contest.contest_id} className="rounded-lg border bg-muted/40 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-semibold">
                    <span style={{ color: contest.lottery_color ?? undefined }}>{contest.lottery_name}</span>{" "}
                    {contest.contest_name}
                  </p>
                  <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                    Venceu em {formatDate(contest.validation_date)}
                  </span>
                </div>
                <ul className="mt-2 divide-y">
                  {contest.products.map((product, index) => (
                    <li key={`${product.name}-${index}`} className="flex justify-between gap-2 py-1">
                      <span className="truncate">{product.name}</span>
                      <span className="shrink-0 font-semibold tabular-nums">{product.quantity}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </AlertDialogDescription>

        <AlertDialogFooter className="flex-col-reverse gap-2 border-t px-4 py-4 sm:flex-row sm:justify-end sm:px-6">
          <Button
            variant="outline"
            className={appDialogSecondaryButton}
            disabled={acknowledging}
            onClick={() => acknowledge(true)}
          >
            Ciente e abrir estoque
          </Button>
          <AlertDialogAction
            onClick={() => acknowledge()}
            disabled={acknowledging}
            className="bg-red-600 text-white hover:bg-red-700"
          >
            Ciente
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
